import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, MapPin, Clock, Wallet, AlertTriangle } from "lucide-react";
import { api, rp } from "@/lib/api";
import { Input } from "@/components/ui/input";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

const loadSnap = (url, clientKey) => new Promise((resolve, reject) => {
  if (window.snap) return resolve(window.snap);
  const el = document.createElement("script");
  el.src = url;
  el.setAttribute("data-client-key", clientKey);
  el.onload = () => resolve(window.snap);
  el.onerror = reject;
  document.body.appendChild(el);
});

const slots = (open, latest) => {
  const out = [];
  const [oh, om] = (open || "10:00").split(":").map(Number);
  const [lh, lm] = (latest || "21:30").split(":").map(Number);
  for (let m = oh * 60 + om; m <= lh * 60 + lm; m += 30) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  }
  return out;
};

const Card = ({ label, value, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4">
    <div className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1 font-display text-2xl" data-testid={testid}>{value}</div>
  </div>
);

const Line = ({ label, value, strong }) => (
  <div className={`flex justify-between gap-4 ${strong ? "font-semibold" : ""}`}>
    <span className={strong ? "" : "text-ink-soft"}>{label}</span><span>{value}</span>
  </div>
);

export default function PartnerHub() {
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [quote, setQuote] = useState(null);
  const [created, setCreated] = useState(null);
  const [rows, setRows] = useState([]);
  const [com, setCom] = useState(null);
  const [busy, setBusy] = useState("");
  const [hubId, setHubId] = useState("");
  const [f, setF] = useState({
    service_mode: "play_at_venue",
    rental_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    start_time: "14:00", package_id: "", quantity: 1, extra_hours: 0, addon_ids: [],
    customer_name: "", customer_whatsapp: "",
  });
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setQuote(null); setCreated(null); };

  const loadRows = useCallback(() => {
    api.get("/partner/venue-bookings", { params: { hub_id: hubId || undefined } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/partner/commissions").then((r) => setCom(r.data)).catch(() => setCom(null));
  }, [hubId]);

  useEffect(() => {
    api.get("/partner/me", { params: { hub_id: hubId || undefined } })
      .then((r) => { setMe(r.data); if (!hubId) setHubId(r.data.hub.id); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat data Partner"));
  }, [hubId]);
  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    if (!f.rental_date || !f.start_time || !hubId) return;
    api.get("/partner/venue-options", { params: {
      rental_date: f.rental_date, start_time: f.start_time, quantity: f.quantity,
      hub_id: hubId, service_mode: f.service_mode, extra_hours: f.extra_hours } })
      .then((r) => {
        setOpts(r.data);
        setF((p) => {
          const cur = (r.data.packages || []).find((x) => x.id === p.package_id);
          if (cur?.eligible) return p;
          const first = (r.data.packages || []).find((x) => x.eligible);
          return { ...p, package_id: first?.id || "" };
        });
      })
      .catch(() => setOpts(null));
  }, [f.rental_date, f.start_time, f.quantity, f.service_mode, f.extra_hours, hubId]);

  const timeOptions = useMemo(
    () => slots(me?.schedule?.hours?.[new Date(f.rental_date).getDay() === 0 ? 6 : new Date(f.rental_date).getDay() - 1]?.open,
                me?.schedule?.latest_pickup),
    [me, f.rental_date]);

  const act = async (key, fn) => {
    setBusy(key);
    try { return await fn(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };

  const getQuote = () => act("quote", async () => {
    const r = await api.post("/partner/venue-quote", { ...f, hub_id: hubId });
    setQuote(r.data);
    if (r.data.hub_errors?.length) toast.error(r.data.hub_errors[0]);
  });

  const createAndPay = () => act("create", async () => {
    const b = created || (await api.post("/partner/venue-bookings", { ...f, hub_id: hubId })).data;
    setCreated(b);
    loadRows();
    const p = (await api.post(`/partner/venue-bookings/${b.booking_code}/pay`)).data;
    const snap = await loadSnap(p.snap_js_url, p.client_key);
    snap.pay(p.snap_token, {
      onSuccess: () => { toast.success("Pembayaran sandbox sukses"); setCreated(null); setOpen(false); loadRows(); },
      onPending: () => { toast.info("Menunggu pembayaran"); loadRows(); },
      onError: () => { toast.error("Pembayaran gagal"); loadRows(); },
      onClose: () => loadRows(),
    });
  });

  if (!me) return <div className="text-ink-soft">Memuat…</div>;

  const packages = opts?.packages || [];
  const chosen = packages.find((p) => p.id === f.package_id);
  const today = me.schedule?.hours?.[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
  const rule = opts?.commission?.rule;
  const ruleText = rule
    ? (rule.mode === "percentage" ? `${rule.value}%`
      : `${rp(rule.value)}${rule.per_unit ? " /meja" : " /booking"}`) : "—";

  return (
    <div className="space-y-6" data-testid="partner-hub">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Partner Hub</h1>
        <p className="mt-1 text-sm text-ink-soft flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-lm-red" />
            <span data-testid="partner-hub-name">{me.hub.name}</span></span>
          <span className="inline-flex items-center gap-1.5"><Clock size={14} />
            Hari ini {today?.closed ? "TUTUP" : `${today?.open}–${today?.close}`} · mulai terakhir {me.schedule.latest_pickup}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="partner-rule-summary">
            <Wallet size={14} /> Komisi {opts?.commission?.category_label || "—"}: {ruleText}
          </span>
        </p>
        {(me.hubs || []).length > 1 && (
          <select className={`${sel} mt-3 max-w-xs`} data-testid="partner-hub-switcher" value={hubId}
            onChange={(e) => { setHubId(e.target.value); setOpen(false); setCreated(null); setQuote(null); }}>
            {me.hubs.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}{h.play_at_venue_enabled ? "" : " (tanpa Main di Venue)"}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="rounded-lg border-2 border-lm-red bg-lm-red/10 px-4 py-3 flex items-start gap-2"
        data-testid="partner-sandbox-warning">
        <AlertTriangle size={18} className="text-lm-red shrink-0 mt-0.5" />
        <span className="text-sm font-semibold text-lm-red">{me.warning}</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card label="Komisi terkumpul" value={rp((com?.totals?.earned || 0) + (com?.totals?.paid || 0))} testid="partner-commission-earned" />
        <Card label="Komisi menunggu bayar" value={rp(com?.totals?.pending || 0)} testid="partner-commission-pending" />
        <Card label="Booking dibuat" value={rows.length} testid="partner-venue-count" />
      </div>

      {com?.rules && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="partner-rules">
          {Object.entries(com.rules).map(([cat, r]) => (
            <div key={cat} className="bg-white border border-black/10 rounded-lg p-4 text-sm"
              data-testid={`partner-rule-${cat}`}>
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">{r.label}</div>
              <div className="mt-1 font-semibold">
                {r.enabled ? (r.mode === "percentage" ? `${r.value}%`
                  : `${rp(r.value)}${r.per_unit ? " per meja" : " per booking"}`) : "Tidak aktif"}
              </div>
              <div className="text-[11px] text-ink-soft">
                Ditetapkan owner · hanya bisa dilihat{r.effective_from ? ` · berlaku ${r.effective_from.slice(0, 10)}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      <button data-testid="venue-booking-btn" onClick={() => { setOpen(!open); setCreated(null); setQuote(null); }}
        className="lm-btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">
        <Plus size={16} /> Buat Booking
      </button>

      {open && (
        <div className="bg-white border border-black/10 rounded-lg p-5" data-testid="venue-form">
          <div className="flex flex-wrap gap-2" data-testid="service-mode-picker">
            {(opts?.service_modes || []).map((m) => (
              <button key={m.key} data-testid={`service-mode-${m.key}`}
                onClick={() => set("service_mode", m.key)}
                className={`px-3.5 py-2 rounded-full text-xs border transition-colors ${
                  f.service_mode === m.key ? "bg-ink text-cream border-ink" : "border-black/15 hover:border-lm-red"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm text-ink-soft" data-testid="venue-mode-note">
            {f.service_mode === "play_at_venue"
              ? `Sesi bermain di ${me.hub.name}. Meja tidak dibawa keluar, jadi sesi harus selesai sebelum Hub tutup dan tidak ada perpanjangan menginap. Tanpa deposit.`
              : "Sewa keluar: meja dibawa customer. Paket harian dan menginap tersedia, deposit mengikuti aturan yang sama seperti booking website."}
          </p>

          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Tanggal</span>
              <Input type="date" className="mt-1 h-10" data-testid="venue-date" value={f.rental_date}
                min={new Date().toISOString().slice(0, 10)} onChange={(e) => set("rental_date", e.target.value)} /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Jam mulai</span>
              <select className={`${sel} mt-1`} data-testid="venue-time" value={f.start_time}
                onChange={(e) => set("start_time", e.target.value)}>
                {timeOptions.map((x) => <option key={x} value={x}>{x}</option>)}
              </select></label>
            <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Jumlah meja</span>
              <Input type="number" min={1} className="mt-1 h-10" data-testid="venue-qty" value={f.quantity}
                onChange={(e) => set("quantity", Math.max(1, Number(e.target.value) || 1))} /></label>

            <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Paket</span>
              <select className={`${sel} mt-1`} data-testid="venue-package" value={f.package_id}
                onChange={(e) => set("package_id", e.target.value)}>
                {packages.length === 0 && <option value="">Memuat paket…</option>}
                {packages.map((p) => (
                  <option key={p.id} value={p.id} disabled={!p.eligible}
                    data-testid={`venue-package-opt-${p.id}`}>
                    {p.name} · {rp(p.promo_price || p.normal_price)}
                    {p.eligible ? ` · selesai ${String(p.ends_at).slice(11, 16)}`
                      : ` — ${p.ineligible_reason}`}
                  </option>
                ))}
              </select></label>
            <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">
              Jam tambahan {opts?.extra_hour_rate ? `(${rp(opts.extra_hour_rate)}/jam/meja)` : ""}</span>
              <Input type="number" min={0} max={24} className="mt-1 h-10" data-testid="venue-extra-hours"
                value={f.extra_hours}
                onChange={(e) => set("extra_hours", Math.max(0, Math.min(24, Number(e.target.value) || 0)))} /></label>
          </div>

          {(opts?.addons || []).length > 0 && (
            <div className="mt-4" data-testid="venue-addons">
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">Add-on</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {opts.addons.map((a) => {
                  const on = f.addon_ids.includes(a.id);
                  return (
                    <button key={a.id} type="button" data-testid={`venue-addon-${a.id}`}
                      onClick={() => set("addon_ids", on ? f.addon_ids.filter((x) => x !== a.id)
                        : [...f.addon_ids, a.id])}
                      className={`px-3 py-1.5 rounded-full text-[11px] border ${
                        on ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
                      {a.name} · {rp(a.price)}{a.price_type === "per_hour" ? "/jam" : a.price_type === "per_unit" ? "/meja" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Nama customer</span>
              <Input className="mt-1 h-10" data-testid="venue-customer-name" value={f.customer_name}
                onChange={(e) => set("customer_name", e.target.value)} /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">WhatsApp customer</span>
              <Input className="mt-1 h-10" placeholder="08xxxxxxxxxx" data-testid="venue-customer-wa"
                value={f.customer_whatsapp} onChange={(e) => set("customer_whatsapp", e.target.value)} /></label>
          </div>

          <div className="mt-3 text-xs text-ink-soft" data-testid="venue-units-free">
            {chosen?.available_units != null
              ? `${chosen.available_units}/${chosen.total_units} unit bebas untuk paket ini`
              : `${opts?.available_units ?? "…"}/${opts?.total_units ?? "…"} unit bebas`}
          </div>
          {packages.some((p) => !p.eligible) && (
            <p className="mt-2 text-xs text-amber-700" data-testid="venue-ineligible-note">
              Paket yang tidak bisa dipakai pada jam ini tetap ditampilkan tapi dinonaktifkan, lengkap
              dengan alasannya.
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button data-testid="venue-quote-btn" disabled={!f.package_id || busy === "quote"} onClick={getQuote}
              className="rounded-full border border-black/15 px-5 py-2.5 text-sm disabled:opacity-40">
              {busy === "quote" ? "Menghitung…" : "Hitung harga"}
            </button>
            <button data-testid="venue-create-btn"
              disabled={!f.package_id || !f.customer_name || f.customer_whatsapp.length < 8 || busy === "create"}
              onClick={createAndPay}
              className="lm-btn-primary rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-2">
              {busy === "create" && <Loader2 size={14} className="animate-spin" />}
              {created ? "Bayar sekarang" : "Buat & bayar"}
            </button>
          </div>

          {quote && (
            <div className="mt-4 border border-black/10 rounded-lg p-4 text-sm space-y-1"
              data-testid="venue-quote-total">
              <Line label={`${quote.package_name_snapshot} × ${quote.quantity} meja`} value={rp(quote.rental_subtotal)} />
              {quote.extra_hours > 0 && (
                <Line label={`Jam tambahan ${quote.extra_hours} jam × ${rp(quote.extra_hour_rate_snapshot)} × ${quote.quantity} meja`}
                  value={rp(quote.extra_hours_total)} />
              )}
              {quote.addon_total > 0 && <Line label="Add-on" value={rp(quote.addon_total)} />}
              {quote.pickup_discount > 0 && <Line label="Diskon ambil sendiri" value={`- ${rp(quote.pickup_discount)}`} />}
              {quote.loyalty_discount > 0 && <Line label="Diskon member" value={`- ${rp(quote.loyalty_discount)}`} />}
              {quote.promo_discount > 0 && <Line label="Promo" value={`- ${rp(quote.promo_discount)}`} />}
              <Line label="Subtotal sewa" value={rp(quote.final_rental_total)} />
              {quote.deposit_amount > 0 && <Line label="Deposit (dikembalikan)" value={rp(quote.deposit_amount)} />}
              <Line label="Total bayar" value={rp(quote.total_payment)} strong />
              <div className="pt-1 text-xs text-ink-soft" data-testid="venue-quote-commission">
                Komisi {quote.commission_category_label}: {quote.commission_breakdown} ={" "}
                <b className="text-ink">{rp(quote.commission_estimate)}</b> · dasar {rp(quote.commission_base)} (tanpa deposit)
              </div>
              <div className="text-xs text-ink-soft">
                Selesai {String(quote.nominal_package_end).slice(11, 16)} · kembali paling lambat{" "}
                {String(quote.operational_return_due).slice(5, 16).replace("T", " ")}
                {quote.return_shifted ? " (disesuaikan jam Hub)" : ""}
              </div>
            </div>
          )}
          {created && (
            <p className="mt-3 text-xs text-ink-soft" data-testid="venue-created-note">
              Booking {created.booking_code} dibuat · unit terkunci · pembayaran ditahan {me.hold_minutes} menit.
            </p>
          )}
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold">Booking yang saya buat</h2>
        <div className="mt-3 overflow-x-auto bg-white border border-black/10 rounded-lg">
          <table className="w-full text-sm" data-testid="venue-bookings-table">
            <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
              <tr><th className="p-3">Kode</th><th className="p-3">Customer</th><th className="p-3">Jadwal</th>
                <th className="p-3">Layanan</th><th className="p-3">Paket</th><th className="p-3">Total</th>
                <th className="p-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {rows.map((b) => (
                <tr key={b.booking_code} data-testid={`venue-row-${b.booking_code}`}>
                  <td className="p-3 font-medium">{b.booking_code}</td>
                  <td className="p-3">{b.customer_name}<div className="text-xs text-ink-soft">{b.customer_whatsapp}</div></td>
                  <td className="p-3">{b.rental_date} {b.start_time}
                    <div className="text-xs text-ink-soft">selesai {String(b.nominal_package_end || "").slice(11, 16)}</div></td>
                  <td className="p-3 text-xs">{b.service_mode_label || b.service_mode}</td>
                  <td className="p-3">{b.package_name_snapshot} × {b.quantity}
                    {b.extra_hours > 0 && <div className="text-xs text-ink-soft">+{b.extra_hours} jam</div>}</td>
                  <td className="p-3">{rp(b.total_payment)}</td>
                  <td className="p-3">{b.payment_status} / {b.booking_status}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-ink-soft">Belum ada booking.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold">Komisi</h2>
        <div className="mt-3 overflow-x-auto bg-white border border-black/10 rounded-lg">
          <table className="w-full text-sm" data-testid="partner-commission-table">
            <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
              <tr><th className="p-3">Booking</th><th className="p-3">Kategori</th><th className="p-3">Dasar</th>
                <th className="p-3">Aturan</th><th className="p-3">Komisi</th><th className="p-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {(com?.rows || []).map((c) => (
                <tr key={c.id} data-testid={`commission-row-${c.booking_code}`}>
                  <td className="p-3 font-medium">{c.booking_code}</td>
                  <td className="p-3 text-xs">{c.category_label || "—"}</td>
                  <td className="p-3">{rp(c.base_amount)}</td>
                  <td className="p-3 text-xs">{c.breakdown || `${c.percent}%`}</td>
                  <td className="p-3">{rp(c.amount)}</td>
                  <td className="p-3">{c.status}</td>
                </tr>
              ))}
              {(com?.rows || []).length === 0 && <tr><td colSpan={6} className="p-4 text-ink-soft">Belum ada komisi.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
